import {
  IQuestionnaireResponse,
  IQuestionnaireResponseItemAnswerComponent,
  QuestionnaireResponseAnswerComponent,
  QuestionnaireResponseItemComponent,
} from '.';
import { IAttachment } from '../Attachment';
import { ICoding, isCodingEqual } from '../Coding';
import { IQuantity } from '../Quantity';
import { QuestionnaireItemEnableWhenComponent } from '../Questionnaire';
import {
  isQuestionnaireItemEnableWhenAttachment,
  isQuestionnaireItemEnableWhenBoolean,
  isQuestionnaireItemEnableWhenCoding,
  isQuestionnaireItemEnableWhenDate,
  isQuestionnaireItemEnableWhenDateTime,
  isQuestionnaireItemEnableWhenDecimal,
  isQuestionnaireItemEnableWhenHasAnwser,
  isQuestionnaireItemEnableWhenInteger,
  isQuestionnaireItemEnableWhenQuantity,
  isQuestionnaireItemEnableWhenReference,
  isQuestionnaireItemEnableWhenString,
  isQuestionnaireItemEnableWhenTime,
  isQuestionnaireItemEnableWhenUri,
} from '../Questionnaire/typeGuards';
import { IReference } from '../Reference';
import {
  isAttachmentEqual,
  isBooleanEqual,
  isDateEqual,
  isDateTimeEqual,
  isDecimalEqual,
  isIntegerEqual,
  isQuantityEqual,
  isReferenceEqual,
  isStringEqual,
  isTimeEqual,
  isUriEqual,
} from './comparator';
import {
  isAttachmentAnswerComponent,
  isBooleanAnswerComponent,
  isCodingAnswerComponent,
  isDateAnswerComponent,
  isDateTimeAnswerComponent,
  isDecimalAnswerComponent,
  isIntegerAnswerComponent,
  isQuantityAnswerComponent,
  isQuestionnaireResponse,
  isQuestionnaireResponseItemAnswer,
  isQuestionnaireResponseItemGroup,
  isQuestionnaireResponseItemTableGroup,
  isReferenceAnswerComponent,
  isStringAnswerComponent,
  isTimeAnswerComponent,
  isUriAnswerComponent,
} from './typeGuards';

export type EnableWhenAnswerTypes =
  | IAttachment
  | boolean
  | ICoding
  | IQuantity
  | Date
  | number
  | IReference
  | string;

export type EnableWhenHandler = (
  rule: QuestionnaireItemEnableWhenComponent,
) => (value?: EnableWhenAnswerTypes) => boolean;

const isEqual = (
  a?: string | number | null,
  b?: string | string[] | number | number[] | null,
) => {
  if (a === b) return true;

  if (!a || !b) return false;

  if (Array.isArray(b)) return b.some((item: string | number) => item === a);

  return false;
};

export const generateSimpleEnableWhenPredicate = (
  rule: QuestionnaireItemEnableWhenComponent,
) => {
  if (isQuestionnaireItemEnableWhenCoding(rule))
    return (value?: string | string[] | null) =>
      !!value && isEqual(rule.answerCoding.code, value);
  if (isQuestionnaireItemEnableWhenDate(rule))
    return (value?: string | string[] | null) =>
      !!value && isEqual(rule.answerDate, value);
  if (isQuestionnaireItemEnableWhenDateTime(rule))
    return (value?: string | string[] | null) =>
      !!value && isEqual(rule.answerDateTime, value);
  if (isQuestionnaireItemEnableWhenDecimal(rule))
    return (value?: string | string[] | null) =>
      !!value && isEqual(rule.answerDecimal, value);
  if (isQuestionnaireItemEnableWhenInteger(rule))
    return (value?: string | string[] | null) =>
      !!value && isEqual(rule.answerInteger, value);
  if (isQuestionnaireItemEnableWhenString(rule))
    return (value?: string | null) =>
      !!value && isEqual(rule.answerString, value);
  if (isQuestionnaireItemEnableWhenTime(rule))
    return (value?: string | string[] | null) =>
      !!value && isEqual(rule.answerTime, value);
  if (isQuestionnaireItemEnableWhenHasAnwser(rule))
    return (value?: EnableWhenAnswerTypes | null) => !!value === rule.hasAnswer;
  throw new Error('Unsupported enable when type');
};

const generatePredicate = (rule: QuestionnaireItemEnableWhenComponent) => {
  if (isQuestionnaireItemEnableWhenAttachment(rule)) {
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isAttachmentAnswerComponent(value) &&
      isAttachmentEqual(rule.answerAttachment, value.valueAttachment);
  }
  if (isQuestionnaireItemEnableWhenBoolean(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isBooleanAnswerComponent(value) &&
      isBooleanEqual(rule.answerBoolean, value.valueBoolean);
  if (isQuestionnaireItemEnableWhenCoding(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isCodingAnswerComponent(value) &&
      isCodingEqual(rule.answerCoding, value.valueCoding);
  if (isQuestionnaireItemEnableWhenDate(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isDateAnswerComponent(value) &&
      isDateEqual(rule.answerDate, value.valueDate);
  if (isQuestionnaireItemEnableWhenDateTime(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isDateTimeAnswerComponent(value) &&
      isDateTimeEqual(rule.answerDateTime, value.valueDateTime);
  if (isQuestionnaireItemEnableWhenDecimal(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isDecimalAnswerComponent(value) &&
      isDecimalEqual(rule.answerDecimal, value.valueDecimal);
  if (isQuestionnaireItemEnableWhenInteger(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isIntegerAnswerComponent(value) &&
      isIntegerEqual(rule.answerInteger, value.valueInteger);
  if (isQuestionnaireItemEnableWhenQuantity(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isQuantityAnswerComponent(value) &&
      isQuantityEqual(rule.answerQuantity, value.valueQuantity);
  if (isQuestionnaireItemEnableWhenReference(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isReferenceAnswerComponent(value) &&
      isReferenceEqual(rule.answerReference, value.valueReference);
  if (isQuestionnaireItemEnableWhenString(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isStringAnswerComponent(value) &&
      isStringEqual(rule.answerString, value.valueString);
  if (isQuestionnaireItemEnableWhenTime(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isTimeAnswerComponent(value) &&
      isTimeEqual(rule.answerTime, value.valueTime);
  if (isQuestionnaireItemEnableWhenUri(rule))
    return (value?: QuestionnaireResponseAnswerComponent | null) =>
      !!value &&
      isUriAnswerComponent(value) &&
      isUriEqual(rule.answerUri, value.valueUri);
  throw new Error('Unsupported enable when type');
};

export const generateEnableWhenPredicate = (
  rule: QuestionnaireItemEnableWhenComponent,
): ((value?: QuestionnaireResponseItemComponent | null) => boolean) => {
  const test = generatePredicate(rule);
  if (isQuestionnaireItemEnableWhenHasAnwser(rule)) {
    return (value?: QuestionnaireResponseItemComponent | null) => {
      if (isQuestionnaireResponseItemGroup(value)) {
        if (rule.hasAnswer && value && value.item.length > 0) return true;
        if (!rule.hasAnswer && (!value || value.item.length <= 0)) return true;
      } else {
        if (rule.hasAnswer && value && value.answer.length > 0) return true;
        if (!rule.hasAnswer && !value) return true;
      }
      return false;
    };
  }
  return (value?: QuestionnaireResponseItemComponent | null) => {
    if (value && isQuestionnaireResponseItemAnswer(value))
      return value.answer.reduce<boolean>((pv, cv) => pv || test(cv), false);
    else return false;
  };
};
export type QuestionResponseMap = {
  [index: string]: QuestionnaireResponseItemComponent;
};

export const getQuestionnaireResponseAnswerItems = (
  questionnaireResponse: IQuestionnaireResponse,
): QuestionResponseMap => {
  const recursiveFn = (
    item: QuestionnaireResponseItemComponent | IQuestionnaireResponse,
  ):
    | IQuestionnaireResponseItemAnswerComponent[]
    | IQuestionnaireResponseItemAnswerComponent => {
    if (isQuestionnaireResponse(item))
      return item.item ? item.item.map(i => recursiveFn(i)).flat() : [];
    if (isQuestionnaireResponseItemTableGroup(item)) {
      return item.item
        ? item.item
            .map(i => {
              const { item: tableGroupItems, ...tableGroupItem } = i;
              const tableGroupSubItems = tableGroupItems
                ? tableGroupItems.map(j => recursiveFn(j))
                : [];
              return [tableGroupItem, ...tableGroupSubItems.flat()];
            })
            .flat()
        : [];
    }
    if (isQuestionnaireResponseItemGroup(item)) {
      return item.item ? item.item.map(i => recursiveFn(i)).flat() : [];
    }

    return item;
  };

  const retV = recursiveFn(questionnaireResponse);
  if (Array.isArray(retV))
    return retV.reduce<QuestionResponseMap>((pv, cv) => {
      if (isQuestionnaireResponseItemAnswer(cv)) pv[cv.linkId] = cv;
      return pv;
    }, {});

  const obj: QuestionResponseMap = {};
  obj[retV.linkId] = retV;

  return obj;
};

const answerItemToString = (
  item: QuestionnaireResponseAnswerComponent,
): string => {
  if (isStringAnswerComponent(item)) {
    return item.valueString;
  }
  if (isCodingAnswerComponent(item)) {
    return item.valueCoding.code || '-1';
  }
  if (isDateAnswerComponent(item)) {
    return item.valueDate;
  }
  if (isIntegerAnswerComponent(item)) {
    return item.valueInteger.toString();
  }
  if (isDecimalAnswerComponent(item)) {
    return item.valueDecimal.toString();
  }

  if (isBooleanAnswerComponent(item)) {
    return item.valueBoolean.toString();
  }
  throw new Error(`Could not convert answer Item to string`);
};

export function createAnswerMap(
  questionnaireResponse: IQuestionnaireResponse,
): { [index: string]: string | string[] } {
  const responseItems = getQuestionnaireResponseAnswerItems(
    questionnaireResponse,
  );

  const items = Object.entries(responseItems)
    .map(([key, item]) => {
      if (!isQuestionnaireResponseItemAnswer(item)) return null;
      if (item.answer.length > 1)
        return {
          key,
          item: item.answer.map(answer => answerItemToString(answer)),
        };
      return { key, item: answerItemToString(item.answer[0]) };
    })
    .reduce<{ [index: string]: string | string[] }>((pv, cv) => {
      if (cv) pv[cv.key] = cv.item;
      return pv;
    }, {});
  return items;
}
